import { useEffect, useRef, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { TemplateBuilderInput, TemplateBuilderInputHandle } from './template-builder-input';
import {
  PROMPT_TEMPLATE_BODY_MAX_LENGTH,
  PROMPT_TEMPLATE_TITLE_MAX_LENGTH,
} from '../../../../../../../../../../backend/src/prompt-template/dto/prompt-template.constants';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreatePromptTemplate } from '@/api/mutations/useCreatePromptTemplate';
import { useUpdatePromptTemplate } from '@/api/mutations/useUpdatePromptTemplate';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { cn } from '@/new/lib/utils';

export interface EditableTemplate {
  id: string;
  matterId?: string;
  title: string;
  body: string;
}

interface TemplateBuilderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: EditableTemplate;
  initialDraft?: { title: string; body: string };
}

function countPlaceholders(value: string) {
  return (value.match(/%?\[[^\]]+\]/g) ?? []).length;
}

export function TemplateBuilderDialog({ open, onOpenChange, template, initialDraft }: TemplateBuilderDialogProps) {
  const { matterId } = useParams();
  const isEditMode = !!template;
  const [label, setLabel] = useState('');
  const [body, setBody] = useState('');
  const inputRef = useRef<TemplateBuilderInputHandle>(null);
  const { mutate: createTemplate, isLoading: isCreating } = useCreatePromptTemplate(matterId);
  const { mutate: updateTemplate, isLoading: isUpdating } = useUpdatePromptTemplate(matterId);
  const isLoading = isCreating || isUpdating;
  const bodyOverLimit = body.length > PROMPT_TEMPLATE_BODY_MAX_LENGTH;
  const labelOverLimit = label.length > PROMPT_TEMPLATE_TITLE_MAX_LENGTH;

  useEffect(() => {
    if (open) {
      setLabel(template?.title ?? initialDraft?.title ?? '');
      setBody(template?.body ?? initialDraft?.body ?? '');
    }
  }, [open, template, initialDraft]);

  const handleSubmit = () => {
    const title = label.trim();
    const trimmedBody = body.trim();
    if (!title || !trimmedBody) return;

    if (isEditMode) {
      updateTemplate(
        { id: template!.id, matterId: template!.matterId, title, body: trimmedBody },
        {
          onSuccess: () => onOpenChange(false),
          onError: (error) => {
            toast.error(error.message || 'Error updating template. Please try again.');
          },
        },
      );
    } else {
      createTemplate(
        { title, body: trimmedBody },
        {
          onSuccess: () => {
            trackCustomEvent(TrackingEventsV2.KimCreateTemplate, {
              matterId,
              placeholderCount: countPlaceholders(trimmedBody),
            });
            onOpenChange(false);
          },
          onError: (error) => {
            toast.error(error.message || 'Error creating template. Please try again.');
          },
        },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-w-3xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            <Icon className="lucide--layout-template" /> {isEditMode ? 'Edit template' : 'Build a template'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update your reusable prompt template. Add placeholders using the '
              : 'Create a reusable prompt template for repeatable workflows. Add placeholders using the '}
            <kbd>[</kbd> key.
          </DialogDescription>
        </DialogHeader>

        <div className="flex min-h-0 w-full flex-1 flex-col">
          <Label htmlFor="template-name" className="mb-3 pl-2">
            Name
          </Label>
          <div className="relative">
            <Input
              id="template-name"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Structured timeline"
              className="pr-24"
            />

            <div
              className={cn(
                'pointer-events-none absolute inset-y-0 right-3 flex items-center gap-1.5 text-xs text-gray-300 tabular-nums duration-150',
                labelOverLimit && 'text-red-500',
              )}
            >
              <Icon
                className={cn(labelOverLimit ? 'opacity-100' : 'opacity-0', 'lucide--alert-circle mb-px duration-150')}
              />
              <span>
                {label.length} / {PROMPT_TEMPLATE_TITLE_MAX_LENGTH}
              </span>
            </div>
          </div>

          <div className="mt-4 mb-1.75 flex items-center justify-between p-2 pr-0.5 pb-1.5">
            <Label className="mb-0">Template Content</Label>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.75 text-xs text-gray-500">
                <Icon className="lucide--info mb-px" />
                Press <kbd className="text-[11px]">[</kbd> to insert a placeholder
              </div>
            </div>
          </div>

          <div className="relative flex min-h-0 w-full flex-1 flex-col">
            <TemplateBuilderInput
              ref={inputRef}
              value={body}
              onChange={setBody}
              placeholder="Build a timeline of events, structured as a table with columns for..."
              className="min-h-[200px] w-full flex-1"
              autoFocus
            />

            <div
              className={cn(
                'absolute right-0 bottom-0 m-1.5 rounded-lg bg-white p-2 px-2.25 text-xs text-gray-300 tabular-nums duration-150',
                bodyOverLimit && 'text-red-500',
              )}
            >
              {body.length} / {PROMPT_TEMPLATE_BODY_MAX_LENGTH}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0">
          <DialogClose asChild>
            <Button>Cancel</Button>
          </DialogClose>
          <Button
            variant="primary"
            className="gap-1.5 pl-3"
            disabled={!label.trim() || !body.trim() || labelOverLimit || bodyOverLimit || isLoading}
            onClick={handleSubmit}
          >
            {isEditMode ? 'Update template' : 'Create template'} <Icon className="lucide--chevron-right" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
